import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAnnualProgressCrossTopicsForTerm } from '../../hooks/useAnnualProgressCrossTopicsForTerm';
import {
  alignCrossTopicCounts,
  countCrossTopics,
} from '../../utils/annualCrossTopicCounts';
import {
  clearAnnualProgressCrossTopicsForTerm,
  publishAnnualProgressCrossTopics,
  publishAnnualProgressCrossTopicDetailRows,
  readAnnualProgressCrossTopicDetailRows,
} from '../../utils/annualProgressCrossTopicRegistry';
import {
  expandCrossTopicDraftsForSyncPrediction,
  mergeCrossTopicDraftsIntoDetailRows,
  type AnnualCrossTopicDetailRow,
  type CrossTopicDraftEntry,
} from '../../utils/annualProgressCrossTopicExtract';
import {
  PAGE_ZOOM_CHANGED_EVENT,
  clientPointToLayoutPoint,
  clientRectToLayoutRect,
  getLayoutViewportSize,
} from '../../utils/pageZoom';
import {
  PROGRAM_PROGRESS_SAVED_EVENT,
  type ProgramProgressSavedDetail,
} from '../../utils/programProgressSavedEvents';
import type { Grade } from '../../types/domain';
import { gradeHasSyncTimetableProgram } from '../../utils/trackProgramModeStorage';

export type CrossTopicCountFloatingModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  grades: Grade[];
  academicYear: number;
  programGradeId: string;
  currentTermNumber: 1 | 2;
  /** 현재 편제 표 초안(실시간) — annualCellKey별 주제어 */
  currentTermDraftEntries: CrossTopicDraftEntry[];
  /** 현재 표가 동기화 미러 뷰이면 true */
  currentTermIsSyncMirror?: boolean;
  /** 미러→대표 annualCellKey */
  syncMirrorRepAnnualCellKeyByMirrorKey?: Record<string, string>;
  /** 대표→미러 annualCellKey[] */
  syncMirrorMirrorAnnualCellKeysByRepKey?: Record<string, string[]>;
  schoolScopedCacheSyncReady?: boolean;
};

function clampPosition(x: number, y: number, width: number, height: number) {
  const pad = 8;
  const { width: vw, height: vh } = getLayoutViewportSize();
  const maxX = Math.max(pad, vw - width - pad);
  const maxY = Math.max(pad, vh - height - pad);
  return {
    x: Math.min(maxX, Math.max(pad, x)),
    y: Math.min(maxY, Math.max(pad, y)),
  };
}

function defaultFloatPosition(width: number, height: number) {
  const tableWrap =
    (document.querySelector('.cp-program-progress-table-wrap') as HTMLElement | null) ??
    (document.querySelector('.cp-annual-progress-table-wrap') as HTMLElement | null);
  const content = document.querySelector('.cp-content') as HTMLElement | null;
  const anchor = tableWrap ?? content;
  if (anchor) {
    const rect = clientRectToLayoutRect(anchor.getBoundingClientRect());
    return clampPosition(rect.left + 12, rect.top + 8, width, height);
  }
  return clampPosition(24, 120, width, height);
}

function filterDetailRowsBySync(
  rows: AnnualCrossTopicDetailRow[],
  includeSyncTimetable: boolean
): AnnualCrossTopicDetailRow[] {
  if (includeSyncTimetable) return rows;
  return rows.filter((row) => !row.isSyncTimetableMirror);
}

function detailCacheUsable(rows: AnnualCrossTopicDetailRow[] | null): boolean {
  if (rows == null) return false;
  if (rows.length === 0) return true;
  return (
    rows.some((row) => typeof row.isSyncTimetableMirror === 'boolean') &&
    rows.some((row) => !!(row.annualCellKey ?? '').trim())
  );
}

const PANEL_W = 300;

const SyncTimetableToggle: React.FC<{
  included: boolean;
  onChange: (included: boolean) => void;
}> = ({ included, onChange }) => (
  <div
    className="cp-neis-learning-upload-filter-toggle cp-cross-topic-count-float-sync-toggle"
    aria-label="동기화 시간표 포함 여부"
  >
    <span className="cp-neis-learning-upload-filter-toggle-label">동기화 시간표</span>
    <button
      type="button"
      className={
        'team-auto-approve-toggle cp-neis-learning-upload-exclude-include-toggle' + (included ? ' on' : '')
      }
      onClick={() => onChange(!included)}
      aria-pressed={included}
      aria-label={`동기화 시간표 ${included ? '포함' : '제외'}`}
    >
      <span className="team-auto-approve-toggle-slider" data-on={!included}>
        제외
      </span>
      <span className="team-auto-approve-toggle-slider" data-on={included}>
        포함
      </span>
    </button>
  </div>
);

const CrossTopicCountFloatingModal: React.FC<CrossTopicCountFloatingModalProps> = ({
  open,
  onClose,
  workspaceId,
  grades,
  academicYear,
  programGradeId,
  currentTermNumber,
  currentTermDraftEntries,
  currentTermIsSyncMirror = false,
  syncMirrorRepAnnualCellKeyByMirrorKey = {},
  syncMirrorMirrorAnnualCellKeysByRepKey = {},
  schoolScopedCacheSyncReady = true,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(() => defaultFloatPosition(PANEL_W, 420));
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const [includeSyncTimetable, setIncludeSyncTimetable] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [registryEpoch, setRegistryEpoch] = useState(0);

  const gid = programGradeId.trim();
  const showSyncTimetableToggle = React.useMemo(() => {
    if (gradeHasSyncTimetableProgram(workspaceId, gid)) return true;
    if (Object.keys(syncMirrorMirrorAnnualCellKeysByRepKey).length > 0) return true;
    if (Object.keys(syncMirrorRepAnnualCellKeyByMirrorKey).length > 0) return true;
    return false;
  }, [gid, syncMirrorMirrorAnnualCellKeysByRepKey, syncMirrorRepAnnualCellKeyByMirrorKey, workspaceId]);
  const effectiveIncludeSyncTimetable = showSyncTimetableToggle && includeSyncTimetable;

  const otherTermNumber: 1 | 2 = currentTermNumber === 1 ? 2 : 1;

  const cachedCurrentDetail = React.useMemo(
    () =>
      open && gid
        ? readAnnualProgressCrossTopicDetailRows(workspaceId, academicYear, gid, currentTermNumber)
        : null,
    [academicYear, currentTermNumber, gid, open, registryEpoch, refreshNonce, workspaceId]
  );

  const cachedOtherDetail = React.useMemo(
    () =>
      open && gid
        ? readAnnualProgressCrossTopicDetailRows(workspaceId, academicYear, gid, otherTermNumber)
        : null,
    [academicYear, gid, open, otherTermNumber, registryEpoch, refreshNonce, workspaceId]
  );

  const needFetchCurrent = open && !!gid && !detailCacheUsable(cachedCurrentDetail);
  const needFetchOther = open && !!gid && !detailCacheUsable(cachedOtherDetail);

  const currentTermFetch = useAnnualProgressCrossTopicsForTerm({
    enabled: needFetchCurrent && grades.length > 0,
    workspaceId,
    grades,
    academicYear,
    termNumber: currentTermNumber,
    programGradeId: gid,
    schoolScopedCacheSyncReady,
    refreshNonce,
  });

  const otherTermFetch = useAnnualProgressCrossTopicsForTerm({
    enabled: needFetchOther && grades.length > 0,
    workspaceId,
    grades,
    academicYear,
    termNumber: otherTermNumber,
    programGradeId: gid,
    schoolScopedCacheSyncReady,
    refreshNonce,
  });

  React.useEffect(() => {
    if (!open || !needFetchCurrent || currentTermFetch.loading) return;
    if (!workspaceId.trim() || !gid) return;
    publishAnnualProgressCrossTopicDetailRows(
      workspaceId,
      academicYear,
      gid,
      currentTermNumber,
      currentTermFetch.detailRows
    );
    publishAnnualProgressCrossTopics(
      workspaceId,
      academicYear,
      gid,
      currentTermNumber,
      currentTermFetch.values
    );
  }, [
    academicYear,
    currentTermFetch.detailRows,
    currentTermFetch.loading,
    currentTermFetch.values,
    currentTermNumber,
    gid,
    needFetchCurrent,
    open,
    workspaceId,
  ]);

  React.useEffect(() => {
    if (!open || !needFetchOther || otherTermFetch.loading) return;
    if (!workspaceId.trim() || !gid) return;
    publishAnnualProgressCrossTopicDetailRows(
      workspaceId,
      academicYear,
      gid,
      otherTermNumber,
      otherTermFetch.detailRows
    );
    publishAnnualProgressCrossTopics(
      workspaceId,
      academicYear,
      gid,
      otherTermNumber,
      otherTermFetch.values
    );
  }, [
    academicYear,
    gid,
    needFetchOther,
    open,
    otherTermFetch.detailRows,
    otherTermFetch.loading,
    otherTermFetch.values,
    otherTermNumber,
    workspaceId,
  ]);

  /** 저장 후 DB 집계 캐시 무효화 → 재조회 */
  React.useEffect(() => {
    if (!open || !gid) return undefined;
    const onSaved = (event: Event) => {
      const detail = (event as CustomEvent<ProgramProgressSavedDetail>).detail;
      if (!detail) return;
      if ((detail.workspaceId ?? '').trim() !== workspaceId.trim()) return;
      if (detail.academicYear !== academicYear) return;
      if ((detail.gradeId ?? '').trim() !== gid) return;
      clearAnnualProgressCrossTopicsForTerm(workspaceId, academicYear, gid, currentTermNumber);
      if (detail.termNumber === otherTermNumber || detail.termNumber === currentTermNumber) {
        clearAnnualProgressCrossTopicsForTerm(workspaceId, academicYear, gid, detail.termNumber);
      }
      setRefreshNonce((n) => n + 1);
      setRegistryEpoch((n) => n + 1);
    };
    window.addEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onSaved as EventListener);
    return () => window.removeEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onSaved as EventListener);
  }, [academicYear, currentTermNumber, gid, open, otherTermNumber, workspaceId]);

  const rawTerm1DbRows = React.useMemo(() => {
    if (!open) return [];
    if (currentTermNumber === 1) {
      return detailCacheUsable(cachedCurrentDetail)
        ? (cachedCurrentDetail as AnnualCrossTopicDetailRow[])
        : currentTermFetch.detailRows;
    }
    return detailCacheUsable(cachedOtherDetail)
      ? (cachedOtherDetail as AnnualCrossTopicDetailRow[])
      : otherTermFetch.detailRows;
  }, [
    cachedCurrentDetail,
    cachedOtherDetail,
    currentTermFetch.detailRows,
    currentTermNumber,
    open,
    otherTermFetch.detailRows,
  ]);

  const rawTerm2DbRows = React.useMemo(() => {
    if (!open) return [];
    if (currentTermNumber === 2) {
      return detailCacheUsable(cachedCurrentDetail)
        ? (cachedCurrentDetail as AnnualCrossTopicDetailRow[])
        : currentTermFetch.detailRows;
    }
    return detailCacheUsable(cachedOtherDetail)
      ? (cachedOtherDetail as AnnualCrossTopicDetailRow[])
      : otherTermFetch.detailRows;
  }, [
    cachedCurrentDetail,
    cachedOtherDetail,
    currentTermFetch.detailRows,
    currentTermNumber,
    open,
    otherTermFetch.detailRows,
  ]);

  const term1Merged = React.useMemo(() => {
    if (currentTermNumber !== 1) {
      return filterDetailRowsBySync(rawTerm1DbRows, effectiveIncludeSyncTimetable);
    }
    const expanded = expandCrossTopicDraftsForSyncPrediction(currentTermDraftEntries, rawTerm1DbRows, {
      syncMirrorView: currentTermIsSyncMirror,
      syncMirrorRepAnnualCellKeyByMirrorKey,
      syncMirrorMirrorAnnualCellKeysByRepKey,
    });
    const base = mergeCrossTopicDraftsIntoDetailRows(rawTerm1DbRows, expanded, {
      draftIsSyncMirror: currentTermIsSyncMirror,
    });
    return filterDetailRowsBySync(base, effectiveIncludeSyncTimetable);
  }, [
    currentTermDraftEntries,
    currentTermIsSyncMirror,
    currentTermNumber,
    effectiveIncludeSyncTimetable,
    rawTerm1DbRows,
    syncMirrorMirrorAnnualCellKeysByRepKey,
    syncMirrorRepAnnualCellKeyByMirrorKey,
  ]);

  const term2Merged = React.useMemo(() => {
    if (currentTermNumber !== 2) {
      return filterDetailRowsBySync(rawTerm2DbRows, effectiveIncludeSyncTimetable);
    }
    const expanded = expandCrossTopicDraftsForSyncPrediction(currentTermDraftEntries, rawTerm2DbRows, {
      syncMirrorView: currentTermIsSyncMirror,
      syncMirrorRepAnnualCellKeyByMirrorKey,
      syncMirrorMirrorAnnualCellKeysByRepKey,
    });
    const base = mergeCrossTopicDraftsIntoDetailRows(rawTerm2DbRows, expanded, {
      draftIsSyncMirror: currentTermIsSyncMirror,
    });
    return filterDetailRowsBySync(base, effectiveIncludeSyncTimetable);
  }, [
    currentTermDraftEntries,
    currentTermIsSyncMirror,
    currentTermNumber,
    effectiveIncludeSyncTimetable,
    rawTerm2DbRows,
    syncMirrorMirrorAnnualCellKeysByRepKey,
    syncMirrorRepAnnualCellKeyByMirrorKey,
  ]);

  const aligned = React.useMemo(() => {
    const term1 = countCrossTopics(term1Merged.map((row) => row.crossTopic));
    const term2 = countCrossTopics(term2Merged.map((row) => row.crossTopic));
    return alignCrossTopicCounts(term1, term2);
  }, [term1Merged, term2Merged]);

  const loading =
    (needFetchCurrent && currentTermFetch.loading) || (needFetchOther && otherTermFetch.loading);

  useEffect(() => {
    if (!open) {
      setIncludeSyncTimetable(false);
      return;
    }
    setPosition(defaultFloatPosition(PANEL_W, 420));
  }, [open, gid, academicYear]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const reclamp = () => {
      const el = panelRef.current;
      const w = el?.offsetWidth ?? PANEL_W;
      const h = el?.offsetHeight ?? 420;
      setPosition((prev) => clampPosition(prev.x, prev.y, w, h));
    };
    reclamp();
    window.addEventListener('resize', reclamp);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, reclamp);
    return () => {
      window.removeEventListener('resize', reclamp);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, reclamp);
    };
  }, [open, aligned.length, loading, effectiveIncludeSyncTimetable]);

  useEffect(() => {
    if (!open || !dragging) return undefined;
    const onMove = (e: PointerEvent) => {
      const el = panelRef.current;
      const w = el?.offsetWidth ?? PANEL_W;
      const h = el?.offsetHeight ?? 420;
      const pt = clientPointToLayoutPoint(e.clientX, e.clientY);
      setPosition(
        clampPosition(pt.x - dragOffsetRef.current.x, pt.y - dragOffsetRef.current.y, w, h)
      );
    };
    const onUp = () => setDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [open, dragging]);

  const onHeaderPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = clientRectToLayoutRect(el.getBoundingClientRect());
    const pt = clientPointToLayoutPoint(e.clientX, e.clientY);
    dragOffsetRef.current = { x: pt.x - rect.left, y: pt.y - rect.top };
    setDragging(true);
    e.preventDefault();
  };

  if (!open) return null;

  const { height: vh } = getLayoutViewportSize();
  const maxPanelHeight = Math.max(200, Math.floor(vh - position.y - 12));

  return createPortal(
    <div
      ref={panelRef}
      className={
        dragging
          ? 'cp-hours-adjust-checklist-float cp-cross-topic-count-float is-dragging'
          : 'cp-hours-adjust-checklist-float cp-cross-topic-count-float'
      }
      style={{ left: position.x, top: position.y, width: PANEL_W, maxHeight: maxPanelHeight }}
      role="dialog"
      aria-label="범교과 카운팅"
    >
      <div className="cp-hours-adjust-checklist-float-header" onPointerDown={onHeaderPointerDown}>
        <h3 className="cp-hours-adjust-checklist-float-title">범교과 카운팅</h3>
        <button
          type="button"
          className="cp-hours-adjust-checklist-float-close"
          onClick={onClose}
          aria-label="닫기"
        >
          ×
        </button>
      </div>
      <div className="cp-hours-adjust-checklist-float-body">
        {showSyncTimetableToggle ? (
          <div className="cp-cross-topic-count-float-toolbar">
            <SyncTimetableToggle included={includeSyncTimetable} onChange={setIncludeSyncTimetable} />
          </div>
        ) : null}
        <div className="cp-hours-adjust-checklist-float-list-wrap cp-cross-topic-count-float-list-wrap">
          {loading ? (
            <p className="cp-annual-hours-adjust-checklist-empty" role="status" aria-busy="true">
              집계 불러오는 중…
            </p>
          ) : aligned.length === 0 ? (
            <p className="cp-annual-hours-adjust-checklist-empty">집계된 주제어가 없습니다.</p>
          ) : (
            <ul className="cp-annual-hours-adjust-checklist-list cp-cross-topic-count-float-list">
              {aligned.map((row) => (
                <li key={row.topic} className="cp-cross-topic-count-float-item">
                  <span className="cp-cross-topic-count-float-topic">[{row.topic}]</span>
                  <span className="cp-cross-topic-count-float-terms">
                    <span className="cp-cross-topic-count-float-term">
                      <span className="cp-cross-topic-count-float-badge cp-cross-topic-count-float-badge--term1" aria-label="1학기">
                        1
                      </span>
                      <strong>{row.term1 ?? 0}</strong>개
                    </span>
                    <span className="cp-cross-topic-count-float-term">
                      <span className="cp-cross-topic-count-float-badge cp-cross-topic-count-float-badge--term2" aria-label="2학기">
                        2
                      </span>
                      <strong>{row.term2 ?? 0}</strong>개
                    </span>
                    <span className="cp-cross-topic-count-float-term cp-cross-topic-count-float-term--annual">
                      <span className="cp-cross-topic-count-float-badge cp-cross-topic-count-float-badge--annual" aria-label="연간">
                        연간
                      </span>
                      <strong>{row.annual}</strong>개
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CrossTopicCountFloatingModal;
